'use client'

import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { waitForFreestarConsent } from './consent'
import { waitForFreestarPrivacyReady } from './privacy'
import { deleteFreestarSlots, ensureFreestarGlobal, invokeFreestarSlot } from './runtime-state'
import { subscribeToFreestarSlotResult } from './slot-result'
import { trackFreestarEvent } from './telemetry'
import { MOBILE_STICKY_PUSHDOWN_NAVBAR_MOUNT_ID, type FreestarOpportunity, type FreestarSlotConfig } from './types'

const FREESTAR_SCRIPT_SRC = 'https://a.pub.network/giphy-com/pubfig.min.js'
const FREESTAR_CLS_CSS = 'https://a.pub.network/giphy-com/cls.css'
const FREESTAR_PRECONNECTS = [
    'https://a.pub.network',
    'https://b.pub.network',
    'https://c.pub.network',
    'https://d.pub.network',
    'https://btloader.com',
    'https://api.btloader.com',
]
const FREESTAR_HOME_EXIT_DURATION_MS = 200

function FreestarBannerSeparator() {
    return (
        <div
            id="freestar-banner-separator"
            style={{
                borderBottom: '2px solid #212121',
                width: '100vw',
                position: 'relative',
                left: '50%',
                right: '50%',
                marginLeft: '-50vw',
                marginRight: '-50vw',
            }}
        />
    )
}

function FreestarSlot({
    opportunity,
    slot,
    runtimeReady,
    mounted,
    visible,
}: {
    opportunity: FreestarOpportunity
    slot: FreestarSlotConfig
    runtimeReady: boolean
    mounted: boolean
    visible: boolean
}) {
    const invokedSlotId = useRef<string | undefined>(undefined)
    const trackedEmptySlotResult = useRef(false)
    const wasRuntimeReady = useRef(runtimeReady)
    const opportunityRef = useRef(opportunity)
    const slotRef = useRef(slot)
    const [hasFilledSlot, setHasFilledSlot] = useState(false)
    const [mobilePushdownMount, setMobilePushdownMount] = useState<HTMLElement | null>(null)
    const renderTargetId = slot.renderTargetId ?? slot.slotId

    opportunityRef.current = opportunity
    slotRef.current = slot

    useEffect(() => {
        if (slot.kind !== 'mobile_pushdown') {
            return
        }
        setMobilePushdownMount(document.getElementById(MOBILE_STICKY_PUSHDOWN_NAVBAR_MOUNT_ID))
    }, [slot.kind])

    useEffect(() => {
        if (wasRuntimeReady.current && !runtimeReady) {
            invokedSlotId.current = undefined
            trackedEmptySlotResult.current = false
            setHasFilledSlot(false)
        }
        wasRuntimeReady.current = runtimeReady
    }, [runtimeReady])

    useEffect(() => {
        if (invokedSlotId.current === slot.slotId) {
            return
        }
        if (!runtimeReady) {
            return
        }
        if (slot.kind === 'mobile_pushdown' && !mobilePushdownMount) {
            return
        }

        const shouldObserveSlotResult = slot.kind === 'leaderboard_atf' || slot.kind === 'mobile_pushdown'
        const unsubscribe = shouldObserveSlotResult
            ? subscribeToFreestarSlotResult(renderTargetId, (result) => {
                  const currentOpportunity = opportunityRef.current
                  const currentSlot = slotRef.current
                  if (result === 'filled') {
                      setHasFilledSlot(true)
                      trackFreestarEvent({
                          opportunity: currentOpportunity,
                          state: 'slot_filled',
                          slot: currentSlot,
                      })
                      return
                  }
                  if (!trackedEmptySlotResult.current) {
                      trackedEmptySlotResult.current = true
                      trackFreestarEvent({
                          opportunity: currentOpportunity,
                          state: 'slot_empty',
                          slot: currentSlot,
                      })
                  }
              })
            : undefined

        invokedSlotId.current = slot.slotId
        try {
            invokeFreestarSlot(slot)
            trackFreestarEvent({ opportunity, state: 'slot_invoked', slot })
        } catch (error) {
            trackFreestarEvent({ opportunity, state: 'runtime_error', reason: 'runtime_error', slot })
        }
        return unsubscribe
    }, [mobilePushdownMount, renderTargetId, runtimeReady, slot.kind, slot.slotId])

    if (slot.kind === 'mobile_pushdown') {
        if (!mounted || !mobilePushdownMount) {
            return null
        }

        const isMobilePushdownVisible = hasFilledSlot && visible

        return createPortal(
            <div
                className="flex w-full justify-center overflow-hidden transition-[height,opacity] duration-200 motion-reduce:transition-none"
                data-freestar-mobile-pushdown-state={isMobilePushdownVisible ? 'expanded' : 'collapsed'}
                id="freestar-mobile-pushdown-container"
                style={{
                    height: isMobilePushdownVisible ? slot.reservedHeight : 0,
                    opacity: isMobilePushdownVisible ? 1 : 0,
                }}
            >
                <div data-freestar-ad={slot.dataFreestarAd} id={renderTargetId} style={{ textAlign: 'center' }} />
            </div>,
            mobilePushdownMount
        )
    }

    if (slot.kind !== 'leaderboard_atf') {
        return null
    }

    const isLeaderboardVisible = hasFilledSlot && visible

    return (
        <>
            <div
                className="mx-auto hidden w-full justify-center overflow-hidden transition-[height,opacity] duration-200 motion-reduce:transition-none md:flex"
                data-freestar-leaderboard-state={isLeaderboardVisible ? 'expanded' : 'collapsed'}
                id="freestar-leaderboard-container"
                style={{
                    height: isLeaderboardVisible ? slot.reservedHeight : 0,
                    opacity: isLeaderboardVisible ? 1 : 0,
                }}
            >
                <div
                    id={slot.slotId}
                    data-freestar-ad={runtimeReady ? slot.dataFreestarAd : undefined}
                    style={isLeaderboardVisible ? { minWidth: slot.reservedWidth } : undefined}
                />
            </div>
            {isLeaderboardVisible && <FreestarBannerSeparator />}
        </>
    )
}

function FreestarRuntime({
    opportunity,
    active,
}: {
    opportunity: Extract<FreestarOpportunity, { status: 'ready' }>
    active: boolean
}) {
    const [privacyReady, setPrivacyReady] = useState(false)
    const trackedScriptLoaded = useRef(false)

    useEffect(() => {
        let active = true
        ensureFreestarGlobal()
        trackFreestarEvent({ opportunity, state: 'attempt' })

        waitForFreestarConsent().then((consent) => {
            if (!active) {
                return
            }
            if (consent.status === 'allowed') {
                waitForFreestarPrivacyReady(consent.didomi).then(() => {
                    if (active) {
                        setPrivacyReady(true)
                    }
                })
                return
            }
            trackFreestarEvent({ opportunity, state: consent.reason, reason: consent.reason })
        })

        return () => {
            active = false
        }
    }, [opportunity])

    useEffect(() => {
        if (!active) {
            deleteFreestarSlots(opportunity.slots, undefined, () => {
                opportunity.slots.forEach((slot) => {
                    trackFreestarEvent({ opportunity, state: 'runtime_error', reason: 'runtime_error', slot })
                })
            })
        }
    }, [active, opportunity.slots])

    return (
        <>
            {opportunity.slots.map(
                (slot) =>
                    (privacyReady || slot.kind === 'leaderboard_atf') && (
                        <FreestarSlot
                            key={slot.slotId}
                            opportunity={opportunity}
                            mounted={privacyReady}
                            runtimeReady={privacyReady && active}
                            slot={slot}
                            visible={active}
                        />
                    )
            )}
            {privacyReady && active && (
                <>
                    {FREESTAR_PRECONNECTS.map((href) => (
                        <link key={href} rel="preconnect" href={href} />
                    ))}
                    <link rel="stylesheet" href={FREESTAR_CLS_CSS} />
                    <Script
                        id="freestar-pubfig"
                        src={FREESTAR_SCRIPT_SRC}
                        strategy="afterInteractive"
                        onReady={() => {
                            ensureFreestarGlobal().initCallbackCalled = true
                            if (!trackedScriptLoaded.current) {
                                trackedScriptLoaded.current = true
                                trackFreestarEvent({ opportunity, state: 'script_loaded' })
                            }
                        }}
                        onError={() => {
                            trackFreestarEvent({ opportunity, state: 'script_error', reason: 'script_error' })
                        }}
                    />
                </>
            )}
        </>
    )
}

export default function FreestarPlacement({ opportunity }: { opportunity?: FreestarOpportunity }) {
    const pathname = usePathname()
    const trackedSuppression = useRef<string | undefined>(undefined)
    const readyOpportunity = opportunity?.status === 'ready' ? opportunity : undefined
    const [displayedOpportunity, setDisplayedOpportunity] = useState(readyOpportunity)
    const [isExitingForHomepage, setIsExitingForHomepage] = useState(false)

    useEffect(() => {
        if (readyOpportunity) {
            setDisplayedOpportunity(readyOpportunity)
            setIsExitingForHomepage(false)
            return
        }
        if (!displayedOpportunity) {
            return
        }
        if (pathname !== '/') {
            setDisplayedOpportunity(undefined)
            setIsExitingForHomepage(false)
            return
        }

        setIsExitingForHomepage(true)
        const timeout = window.setTimeout(() => {
            setDisplayedOpportunity(undefined)
            setIsExitingForHomepage(false)
        }, FREESTAR_HOME_EXIT_DURATION_MS)
        return () => window.clearTimeout(timeout)
    }, [displayedOpportunity, pathname, readyOpportunity])

    useEffect(() => {
        if (!opportunity || opportunity.status !== 'suppressed') {
            return
        }
        if (opportunity.reason === 'backend_not_cleared') {
            return
        }
        const key = `${opportunity.context.view}:${opportunity.context.deviceType}:${opportunity.reason}`
        if (trackedSuppression.current === key) {
            return
        }
        trackedSuppression.current = key
        trackFreestarEvent({ opportunity, state: opportunity.reason, reason: opportunity.reason })
    }, [opportunity])

    if (!displayedOpportunity) {
        return null
    }

    return <FreestarRuntime active={!!readyOpportunity && !isExitingForHomepage} opportunity={displayedOpportunity} />
}
